import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import {
  getCurrentTimer,
  startTimer as apiStartTimer,
  stopTimer as apiStopTimer,
  assignTaskToTimer as apiAssignTaskToTimer,
  pauseTimer as apiPauseTimer,
  resumeTimer as apiResumeTimer,
  getRecentTimers as apiGetRecentTimers,
} from "../functions/api_fethcers";
import { formatTimeDisplay } from "../utils/timeUtils";
import { useAuthContext } from "./AuthContext";
// Helper to parse ISO timestamp strings as UTC (append 'Z' if missing timezone info)
// so that naive timestamps aren’t mis-parsed in local timezone
const parseTimestamp = (ts) => {
  if (!ts) return null;
  const s = String(ts);
  // if already has Z or +/-HH:MM timezone, leave as-is, else append Z
  const hasTZ = /Z$|[+\-]\d{2}:\d{2}$/.test(s);
  const iso = hasTZ ? s : `${s}Z`;
  return new Date(iso).getTime();
};

// -----------------------------------------------------------------------------
// Context helpers
// -----------------------------------------------------------------------------
const TimerContext = createContext();
export const useTimer = () => useContext(TimerContext);

// -----------------------------------------------------------------------------
// Provider Logic (wrapped by QueryClientProvider higher up in the tree)
// -----------------------------------------------------------------------------
const TimerProviderLogic = ({ children }) => {
  const { isAuthenticated } = useAuthContext();
  const queryClient = useQueryClient();

  // ---------------------------------------------------------------------------
  // Local UI state
  // ---------------------------------------------------------------------------
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [timerInterval, setTimerInterval] = useState(null);
  const [isTimerPanelOpen, setIsTimerPanelOpen] = useState(false);
  const [error, setError] = useState(null);

  // ---------------------------------------------------------------------------
  // React‑Query – Queries
  // ---------------------------------------------------------------------------
  const {
    data: currentTimer,
    isLoading: isLoadingCurrent,
    refetch: refreshCurrentTimer,
  } = useQuery(["currentTimer"], getCurrentTimer, {
    enabled: isAuthenticated(),
    refetchInterval: 30_000,
    staleTime: 25_000,
    onError: (err) => {
      console.error("Error fetching current timer:", err);
      setError(err);
      // Clear state on genuine error
      stopTimerInterval();
      setElapsedSeconds(0);
      clearLocalStorageTimerData();
      setIsTimerPanelOpen(false);
    },
    onSuccess: (timer) => {
      if (timer) {
        // Running timer – open panel
        if (!isTimerPanelOpen) setIsTimerPanelOpen(true);
      } else {
        // API returned null → check for paused timer in localStorage
        const status = localStorage.getItem("timer_status");
        if (status === "paused") {
          // Rebuild paused timer from stored data
          const storedId = localStorage.getItem("timer_id");
          const storedStart = localStorage.getItem("timer_start_time");
          const pausedAt = localStorage.getItem("timer_paused_at");
          const totalPause = Number(
            localStorage.getItem("timer_pause_duration") || 0
          );
          const fakeTimer = {
            _id: storedId,
            id: storedId,
            start_time: new Date(
              parseInt(storedStart || "0", 10)
            ).toISOString(),
            paused_at: new Date(
              parseInt(pausedAt || "0", 10)
            ).toISOString(),
            total_pause_duration: totalPause,
            status: "paused",
          };
          queryClient.setQueryData(["currentTimer"], fakeTimer);
          // Compute and set elapsed at pause
          const startTS = parseTimestamp(fakeTimer.start_time);
          const pauseTS = parseTimestamp(fakeTimer.paused_at);
          const finalElapsed = Math.max(
            0,
            Math.floor((pauseTS - startTS) / 1000) - totalPause
          );
          setElapsedSeconds(finalElapsed);
          if (!isTimerPanelOpen) setIsTimerPanelOpen(true);
        } else {
          // No paused record – close panel
          if (isTimerPanelOpen) setIsTimerPanelOpen(false);
        }
      }
    },
  });

  // recent timers – note: dynamic limit preserved via query key
  const fetchRecentTimersQuery = (limit = 5) =>
    queryClient.fetchQuery(["recentTimers", limit], () =>
      apiGetRecentTimers(limit)
    );

  const { data: recentTimers = [], isLoading: isLoadingRecent } = useQuery(
    ["recentTimers", 5],
    () => apiGetRecentTimers(5),
    {
      enabled: isAuthenticated(),
      staleTime: 5 * 60_000,
      onError: (err) => {
        console.error("Error fetching recent timers:", err);
        setError(err);
      },
    }
  );

  // ---------------------------------------------------------------------------
  // Mutation helper (toasts + error)
  // ---------------------------------------------------------------------------
  const handleMutationError = (err, msg) => {
    console.error(msg, err);
    setError(err);
    toast.error(err?.response?.data?.message || err.message || msg);
  };

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------
  const startMutation = useMutation(apiStartTimer, {
    onSuccess: (newTimer) => {
      queryClient.setQueryData(["currentTimer"], newTimer);
      setIsTimerPanelOpen(true);
      toast.success("Timer started");
    },
    onError: (err) => handleMutationError(err, "Failed to start timer"),
  });

  // Modify the mutation function to correctly handle the array argument from mutateAsync
  const stopMutation = useMutation(
    ([timerId, description]) => apiStopTimer(timerId, description),
    {
      onSuccess: (stoppedTimer) => {
        queryClient.setQueryData(["currentTimer"], null);
        queryClient.invalidateQueries({ queryKey: ["recentTimers"] });
        stopTimerInterval();
        setElapsedSeconds(0);
        clearLocalStorageTimerData();
        setIsTimerPanelOpen(false);
        toast.success("Timer stopped");
      },
      onError: (err) => handleMutationError(err, "Failed to stop timer"),
    }
  );

  // Modify the mutation function to correctly handle the array argument from mutateAsync
  const pauseMutation = useMutation(
    ([timerId, description]) => apiPauseTimer(timerId, description),
    {
      onSuccess: (pausedTimer) => {
        queryClient.setQueryData(["currentTimer"], pausedTimer);
        // record paused timestamp + duration for reload persistence
        if (pausedTimer.paused_at) {
          // store as UTC epoch ms to avoid local TZ skew
          localStorage.setItem(
            "timer_paused_at",
            String(parseTimestamp(pausedTimer.paused_at))
          );
        }
        if (pausedTimer.total_pause_duration) {
          localStorage.setItem(
            "timer_pause_duration",
            pausedTimer.total_pause_duration.toString()
          );
        }
        // record status for page-reload fallback
        localStorage.setItem("timer_status", "paused");
        console.log("[TimerContext] pauseMutation: set timer_status=paused");
        toast.info("Timer paused");
      },
      onError: (err) => handleMutationError(err, "Failed to pause timer"),
    }
  );

  const resumeMutation = useMutation(apiResumeTimer, {
    onSuccess: (resumedTimer) => {
      queryClient.setQueryData(["currentTimer"], resumedTimer);
      localStorage.removeItem("timer_paused_at");
      if (resumedTimer.total_pause_duration) {
        localStorage.setItem(
          "timer_pause_duration",
          resumedTimer.total_pause_duration.toString()
        );
      }
      // update status for page-reload fallback
      localStorage.setItem("timer_status", "running");
      console.log("[TimerContext] resumeMutation: set timer_status=running");
      toast.info("Timer resumed");
    },
    onError: (err) => handleMutationError(err, "Failed to resume timer"),
  });

  // Modify the mutation function to correctly handle the array argument from mutateAsync
  const assignTaskMutation = useMutation(
    ([timerId, taskId, description]) =>
      apiAssignTaskToTimer(timerId, taskId, description),
    {
      onSuccess: (updatedTimer) => {
        queryClient.setQueryData(["currentTimer"], updatedTimer);
        toast.success("Task assigned to timer");
      },
      onError: (err) => handleMutationError(err, "Failed to assign task"),
    }
  );

  // ---------------------------------------------------------------------------
  // Interval helpers
  // ---------------------------------------------------------------------------
  const stopTimerInterval = useCallback(() => {
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }
  }, [timerInterval]);

  const startTimerInterval = useCallback(
    (timer) => {
      stopTimerInterval();
      if (!timer || !timer.start_time) return;

      const startTimeStamp = parseTimestamp(timer.start_time);
      const totalPauseDurationSeconds = timer.total_pause_duration || 0;

      localStorage.setItem("timer_start_time", startTimeStamp.toString());
      localStorage.setItem("timer_id", timer._id || timer.id);
      localStorage.setItem(
        "timer_pause_duration",
        totalPauseDurationSeconds.toString()
      );
      localStorage.setItem("timer_status", timer.status || "running");

      // initial elapsed
      // Compute initial elapsed seconds from UTC-based timestamp
      const initElapsed = Math.max(
        0,
        Math.floor((Date.now() - startTimeStamp) / 1000) - totalPauseDurationSeconds
      );
      setElapsedSeconds(initElapsed);

      const interval = setInterval(() => {
        const elapsed = Math.max(
          0,
          Math.floor((Date.now() - startTimeStamp) / 1000) -
            totalPauseDurationSeconds
        );
        setElapsedSeconds(elapsed);
      }, 1000);

      setTimerInterval(interval);
    },
    [stopTimerInterval]
  );

  // ---------------------------------------------------------------------------
  // Keep interval in sync with currentTimer
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (currentTimer && currentTimer.status === "running") {
      startTimerInterval(currentTimer);
    } else {
      stopTimerInterval();
      // Removed: if (!currentTimer) setElapsedSeconds(0); // Resetting here can cause issues during updates. Reset only on explicit stop.
      if (currentTimer && currentTimer.status === "paused") {
        // Calculate and set elapsed time for paused state using UTC parsing
        const startTS = parseTimestamp(currentTimer.start_time);
        const pauseTS = currentTimer.paused_at
          ? parseTimestamp(currentTimer.paused_at)
          : Date.now();
        const totalPause = currentTimer.total_pause_duration || 0;
        const finalElapsed = Math.max(
          0,
          Math.floor((pauseTS - startTS) / 1000) - totalPause
        );
        setElapsedSeconds(finalElapsed);
      }
    }
    return () => stopTimerInterval();
  }, [currentTimer, startTimerInterval, stopTimerInterval]);

  // ---------------------------------------------------------------------------
  // LOCAL STORAGE FALLBACK (page reload while offline)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isAuthenticated()) return;
    const storedId = localStorage.getItem("timer_id");
    const storedStart = localStorage.getItem("timer_start_time");
    if (storedId && storedStart && !currentTimer) {
      const fakeTimer = {
        _id: storedId,
        start_time: new Date(parseInt(storedStart, 10)).toISOString(),
        total_pause_duration: Number(
          localStorage.getItem("timer_pause_duration") || 0
        ),
        status: localStorage.getItem("timer_status") || "running",
      };
      startTimerInterval(fakeTimer);
    }
  }, []); // run only once

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------
  const clearLocalStorageTimerData = () => {
    localStorage.removeItem("timer_start_time");
    localStorage.removeItem("timer_id");
    localStorage.removeItem("timer_status");
    localStorage.removeItem("timer_pause_duration");
    localStorage.removeItem("timer_paused_at");
  };

  const formattedTime = useMemo(
    () => formatTimeDisplay(elapsedSeconds),
    [elapsedSeconds]
  );

  // ---------------------------------------------------------------------------
  // Public API (keeps original signatures!)
  // ---------------------------------------------------------------------------
  const startTimer = (data = {}) => startMutation.mutateAsync(data);

  const stopTimer = (description) => {
    if (!currentTimer) return Promise.resolve(null);
    return stopMutation.mutateAsync([
      currentTimer._id || currentTimer.id,
      description,
    ]);
  };

  const pauseTimer = (description) => {
    if (!currentTimer) return Promise.resolve(null);
    if (currentTimer.status === "paused") return Promise.resolve(currentTimer);
    return pauseMutation.mutateAsync([
      currentTimer._id || currentTimer.id,
      description,
    ]);
  };

  const resumeTimer = () => {
    if (!currentTimer) return Promise.resolve(null);
    if (currentTimer.status !== "paused") return Promise.resolve(currentTimer);
    return resumeMutation.mutateAsync(currentTimer._id || currentTimer.id);
  };

  const assignTaskToTimer = (taskId, description) => {
    if (!currentTimer) return Promise.resolve(null);
    return assignTaskMutation.mutateAsync([
      currentTimer._id || currentTimer.id,
      taskId,
      description,
    ]);
  };

  const fetchRecentTimers = (limit = 5) => fetchRecentTimersQuery(limit);

  const clearError = () => setError(null);

  const value = useMemo(
    () => ({
      // data
      currentTimer,
      isLoading: isLoadingCurrent,
      error,
      recentTimers,
      isLoadingRecent,
      elapsedSeconds,
      formattedTime,
      isTimerPanelOpen,

      // actions
      startTimer,
      stopTimer,
      pauseTimer,
      resumeTimer,
      assignTaskToTimer,
      fetchRecentTimers,
      refreshCurrentTimer,
      toggleTimerPanel: () => setIsTimerPanelOpen((p) => !p),
      clearError,

      // individual mutation flags
      isStarting: startMutation.isLoading,
      isStopping: stopMutation.isLoading,
      isPausing: pauseMutation.isLoading,
      isResuming: resumeMutation.isLoading,
      isAssigningTask: assignTaskMutation.isLoading,
    }),
    [
      currentTimer,
      isLoadingCurrent,
      error,
      recentTimers,
      isLoadingRecent,
      elapsedSeconds,
      formattedTime,
      isTimerPanelOpen,
      startMutation.isLoading,
      stopMutation.isLoading,
      pauseMutation.isLoading,
      resumeMutation.isLoading,
      assignTaskMutation.isLoading,
    ]
  );

  return (
    <TimerContext.Provider value={value}>{children}</TimerContext.Provider>
  );
};

// -----------------------------------------------------------------------------
// Public Provider – assume react‑query's QueryClientProvider is higher up
// -----------------------------------------------------------------------------
export const TimerProvider = ({ children }) => (
  <TimerProviderLogic>{children}</TimerProviderLogic>
);
